import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { IconButton } from "common/core/button/icon_button";
import { useFormState, type UseFormReturn } from "common/core/form";
import { FormattedFieldError } from "common/core/form/error";
import { ObfuscatedInput } from "common/core/form/obfuscated_input";
import { SENSITIVE_CLASS } from "util/sensitive";

import type { SignerSSNFormValues } from "./modal";

type Props = {
  fieldName: string;
  fieldLabel: string;
  className?: string;
  hasSsn: boolean;
};

type InnerProps = Props & UseFormReturn<SignerSSNFormValues>;

const normalizeSsn = (ssn: string | undefined) => {
  if (typeof ssn !== "string") {
    return "";
  }
  return ssn.replace(/\D/g, "");
};

const formatSsnDisplay = (ssn: string) => {
  const truncated = normalizeSsn(ssn).slice(0, 9);

  if (truncated.length >= 9) {
    return `${truncated.slice(0, 3)} - ${truncated.slice(3, 5)} - ${truncated.slice(5, 9)}`;
  } else if (truncated.length >= 5) {
    return `${truncated.slice(0, 3)} - ${truncated.slice(3, 5)} - ${truncated.slice(5)}`;
  } else if (truncated.length >= 3) {
    return `${truncated.slice(0, 3)} - ${truncated.slice(3)}`;
  }
  return truncated;
};

const messages = defineMessages({
  socialSecurityNumber: {
    id: "09111c98-3ac0-4c0c-9996-52cd8a4992a2",
    defaultMessage: "Social security number for {fieldLabel}",
  },
  ssnError: {
    id: "31807d7f-e6ac-43e6-85f0-3ec6e64be386",
    defaultMessage: "{fieldLabel} SSN must be 9 digits",
  },
});

/**
 * @deprecated
 */
export function DeprecatedSignerSsnField(props: InnerProps) {
  const { hasSsn, fieldLabel, className, fieldName, control, resetField } = props;
  const [showFormField, setShowFormField] = useState(!hasSsn);

  const { errors } = useFormState({ control });
  const error = errors[fieldName];

  const intl = useIntl();

  const clearSsnValue = () => {
    resetField(fieldName);
    setShowFormField(true);
  };
  const handleSsnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeSsn(e.target.value).slice(0, 9);
    return formatSsnDisplay(normalized);
  };

  return (
    <div className="SignerSsnFormRow">
      <div className="Form-section-header">
        <div className={SENSITIVE_CLASS}>{fieldLabel}</div>
      </div>
      {showFormField ? (
        <div className={className}>
          <ObfuscatedInput
            id={fieldName}
            name={fieldName}
            inputMode="numeric"
            control={control}
            disableMask={false}
            mask="••• - •• - ••••"
            onChange={handleSsnChange}
            aria-label={intl.formatMessage(messages.socialSecurityNumber, { fieldLabel })}
            aria-invalid={!!error}
            maxLength={15}
            rules={{
              validate: (value: string | undefined) => {
                if (!value) {
                  return true;
                }
                const normalized = normalizeSsn(value);
                return (
                  normalized.length === 9 || intl.formatMessage(messages.ssnError, { fieldLabel })
                );
              },
            }}
            onTransform={(value: string) => {
              const normalized = normalizeSsn(value);
              return formatSsnDisplay(normalized);
            }}
            data-automation-id={fieldName}
          />
          {error ? (
            <FormattedFieldError className={SENSITIVE_CLASS} inputName={fieldName} error={error} />
          ) : null}
        </div>
      ) : (
        <div className="Form-row">
          <div className="ssn-value-field">
            <input
              id={fieldName}
              type="text"
              className="Form-input--readonly"
              value="••• - •• - ••••"
              disabled
              readOnly
            />
            <IconButton
              name="x-mark"
              className="remove"
              onClick={clearSsnValue}
              variant="tertiary"
              color="action"
              label={
                <FormattedMessage
                  id="79c0e332-153c-4f2e-976e-1436995f6bba"
                  defaultMessage="Remove social security number"
                />
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
